import React, { createContext, useContext } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';

// True while the shell is still hydrating the browser's data layer. Every widget
// stays under its skeleton until then, so the hydration lag and the page remount
// that follows it happen out of sight (BD-1196).
export const HydratingContext = createContext(false);

const column = { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 };

// Loading wins over failure: a retry in flight shows the skeleton, not the error.
export function queryStatus(queries, pending = false) {
    const qs = queries.filter(Boolean);
    const loading = pending || qs.some((q) => q.isFetching);
    return { loading, failed: loading ? [] : qs.filter((q) => q.isError) };
}

const LoadingCover = () => (
    <Skeleton
        role="status"
        aria-label="Loading"
        variant="rounded"
        animation="wave"
        sx={{ position: 'absolute', inset: 0, height: 'auto', transform: 'none', '&::after': { animationDuration: '2.4s' } }}
    />
);

const ErrorCover = ({ failed }) => (
    <Box
        role="alert"
        sx={{
            position: 'absolute', inset: 0, borderRadius: 1, bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2, textAlign: 'center',
        }}
    >
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>This couldn't be loaded.</Typography>
        <Button size="small" variant="outlined" onClick={() => failed.forEach((q) => q.refetch())}>Retry</Button>
    </Box>
);

// BD-1196: while any of a widget's queries is fetching, a wave skeleton covers it.
// The widget stays mounted underneath (hidden), so the skeleton takes its normal
// size and nothing shifts when the data lands. A failed query hides the figures
// and offers Retry, rather than showing stale or partial numbers.
// `fill`: the widget stretches to fill a flex column (equal-height rows), so the
// wrapper has to pass that stretch through. `loading`: a load that isn't a query,
// e.g. the browser's first data hydration.
export default function WidgetState({ queries = [], loading: pending, fill, children }) {
    const hydrating = useContext(HydratingContext);
    const { loading, failed } = queryStatus(queries, pending || hydrating);
    const covered = loading || failed.length > 0;
    const layout = fill ? column : undefined;

    return (
        <Box sx={{ position: 'relative', ...layout }}>
            <Box aria-hidden={covered || undefined} style={{ visibility: covered ? 'hidden' : 'visible' }} sx={layout}>
                {children}
            </Box>
            {loading && <LoadingCover />}
            {failed.length > 0 && <ErrorCover failed={failed} />}
        </Box>
    );
}
